// Formato de los valores que manda un sitio público (ya proyectados por el
// servidor: relaciones como {id, label}) y mensajes de error para mostrar.

import { ConvexError } from "convex/values";
import { format, isValid, parseISO } from "date-fns";
import { es } from "date-fns/locale";
import type { DeferredBlock, FieldMeta, PageBlock, ResolvedBlock } from "@/convex/lib/sites/resolve";

export type { DeferredBlock, FieldMeta, PageBlock, ResolvedBlock };
export type SiteBlockOf<T extends ResolvedBlock["type"]> = T extends "heading" | "text"
  ? Extract<ResolvedBlock, { text: string }>
  : Extract<ResolvedBlock, { type: T }>;

const EMPTY = "—";
const numberFormat = new Intl.NumberFormat("es", { maximumFractionDigits: 2 });
const moneyFormat = new Intl.NumberFormat("es", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export function isEmptyValue(value: unknown): boolean {
  return value == null || value === "" || (Array.isArray(value) && value.length === 0);
}

function formatDate(value: unknown): string | null {
  const date =
    typeof value === "number" ? new Date(value) : typeof value === "string" ? parseISO(value) : null;
  if (!date || !isValid(date)) return null;
  // Con hora solo si la trae (un campo fecha guarda «2026-10-01»).
  const withTime = typeof value === "string" ? value.includes("T") : false;
  return format(date, withTime ? "d MMM yyyy, HH:mm" : "d MMM yyyy", { locale: es });
}

/** Texto de un valor según el tipo del campo. */
export function formatSiteValue(value: unknown, type?: string): string {
  if (isEmptyValue(value)) return EMPTY;
  if (Array.isArray(value)) return value.map((v) => formatSiteValue(v, type)).join(", ");
  if (typeof value === "object" && value && "label" in value) return String((value as { label: unknown }).label ?? EMPTY);
  if (typeof value === "boolean") return value ? "Sí" : "No";
  if (type === "checkbox" || type === "boolean") return value === "true" ? "Sí" : value === "false" ? "No" : String(value);
  if (type === "date" || type === "datetime") return formatDate(value) ?? String(value);
  if (typeof value === "number") return type === "currency" ? moneyFormat.format(value) : numberFormat.format(value);
  if (typeof value === "object") return EMPTY;
  return String(value);
}

/** Mensaje para mostrar: el de un ConvexError (ya en español) o `fallback`. */
export function siteErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof ConvexError) {
    const data = error.data as unknown;
    if (typeof data === "string" && data.trim()) return data;
    if (data && typeof data === "object" && typeof (data as { message?: unknown }).message === "string") {
      return (data as { message: string }).message;
    }
  }
  return fallback;
}
